<template>
  <v-card class="gallery-view">
    <gallery-view-toolbar
      :editable="editable"
      :event="event"
      :sensorType="sensorType"
      :canDownload="!event.updating"
      :downloadOptions="downloadOptions"
      :title="title"
      @download="download"
      @update="(attr) => $emit('updateEvent', attr)"
      @close="$emit('close')"
    >
      <template v-if="editable">
        <template v-if="!event.updating">
          <toolbar-button
            class="hidden-sm-and-down"
            icon="fa-edit"
            @click.native.stop="editing = true;"
            data-testid="edit"
          >
            {{ $t('menu.edit') }}
          </toolbar-button>
          <toolbar-button
            icon="fa-trash"
            @click.native.stop="$emit('deleteEvent')"
          >
            {{ $t('menu.delete') }}
          </toolbar-button>
        </template>
        <v-menu offset-y v-else-if="showTimeoutMenu">
          <template #activator="{ on, attrs }">
            <v-tooltip bottom>
              <template #activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                <toolbar-button
                  class="hidden-sm-and-down"
                  icon="fa-edit"
                  warning
                  v-bind="{ ...attrs, ...tooltipAttrs }"
                  v-on="{ ...on, ...tooltipOn }"
                  data-testid="edit-timeout-warning"
                >
                  {{ $t('menu.edit') }}
                </toolbar-button>
              </template>
              {{ $t('msg.timeout.detail') }}
            </v-tooltip>
          </template>
          <v-list>
            <v-list-item @click="retry" data-testid="retry">
              <v-list-item-title>{{ $t('menu.retry') }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click="editing = true" data-testid="edit-again">
              <v-list-item-title>{{ $t('menu.edit_again') }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click="confirmDismiss" data-testid="dismiss">
              <v-list-item-title>{{ $t('menu.dismiss') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <toolbar-button icon="fa-refresh" disabled v-else>
          {{ $t('menu.updating') }}
        </toolbar-button>
      </template>
    </gallery-view-toolbar>
    <layout
      class="mb-3"
      :hasPrevious="hasPrevious"
      :hasNext="hasNext"
      @previous="$emit('previous')"
      @next="$emit('next')"
    >
      <template #top>
        <gallery-view-header
          :editable="editable"
          :event="event"
          @updateEvent="$emit('updateEvent', $event)"
        />
      </template>
      <div class="gallery-view__image">
        <div class="gallery-view__image__container">
          <picked-insects-draw
            :imageSrc="event.attachments['photo']"
            :detectionsUrl="detectionsUrl"
            :hideCanvas="viewOriginal"
            :originalImageWidth='ORIGINAL_IMAGE_WIDTH'
            :originalImageHeight='ORIGINAL_IMAGE_HEIGHT'
            pickedDetectionType="all"
            @save="save"
            ref="drawer"
          />
        </div>
      </div>
      <div class="d-flex justify-center">
        <replacement-notification
          class="mt-3"
          :trap_replaced_on="trap_replaced_on"
          :trap_effective_days="trap_effective_days"
        />
      </div>
      <template #side>
        <div class="result pa-2">
          <div class="pl-1 mb-2">
            <v-checkbox
              class="mt-1"
              v-model="viewOriginal"
              :label="$t('field.original')"
              hide-details
              color="primary"
            />
          </div>
          <div class="pt-2 px-2">
            <div class="result-item">
              <span>{{ $t('field.total') }}</span>
              <span class="mx-1">:</span>
              <span>{{ event.count }}</span>
            </div>
            <insect-count-difference class="result-item" :event="event" />
          </div>
          <hr />
          <threshold-alert-status
            class="pa-2"
            :event="event"
            :editable="editable"
            @updateThreshold="$emit('updateThreshold', $event)"
          />
        </div>
      </template>
    </layout>
    <slot name="thumbnails" />
    <insect-detections-editor
      content-class="insect-detections-editor--fly-count"
      :event="event"
      :imageSrc="event.attachments['photo']"
      :detectionsUrl="detectionsUrl"
      :originalImageWidth='ORIGINAL_IMAGE_WIDTH'
      :originalImageHeight='ORIGINAL_IMAGE_HEIGHT'
      v-model="editing"
      @updated="$emit('pollEvent')">
      <template #header>
        <content-header class="w-100" :title="title" titleClass="title my-1">
          <template #icon><sensor-icon :sensorType="sensorType" /></template>
          <template #subtitle>
            <div class="title details">
              {{ timestamp.date }}<span class="ml-3" />{{ timestamp.time }} {{ $t('field.total') }}: {{ event.count }}
            </div>
          </template>
        </content-header>
      </template>
    </insect-detections-editor>
    <ConfirmDialog
      v-model="dismissConfirmOpen"
      :message="$t('msg.dismiss.confirm')"
      @yes="executeDismiss"
      data-testid="dismiss-confirm-dialog"
    />
  </v-card>
</template>

<i18n lang="yaml">
ja:
  menu:
    edit: 修正する
    delete: 削除
    updating: 更新中
    retry: '再依頼'
    edit_again: '編集し直す'
    dismiss: '無視する'
  field:
    original: 撮影画像を表示
    total: 頭数合計
  msg:
    download:
      failed: 画像のダウンロードに失敗しました。
    timeout:
      detail: '編集内容の反映に時間がかかっています。操作を選択できます'
    retry:
      success: '再依頼を受け付けました'
      failed: '再依頼に失敗しました'
    dismiss:
      confirm: '編集結果の反映は保証されません。「更新中」の表示のみ解除します。よろしいですか?'
      failed: '無視処理に失敗しました'

en:
  menu:
    edit: Edit
    delete: Delete
    updating: Updating
    retry: 'Retry'
    edit_again: 'Edit again'
    dismiss: 'Dismiss'
  field:
    original: Show original photo
    total: Total
  msg:
    download:
      failed: Failed to download image.
    timeout:
      detail: 'The edit is taking longer than expected. Choose an action.'
    retry:
      success: 'Retry accepted.'
      failed: 'Retry failed.'
    dismiss:
      confirm: 'The edit result may not be reflected. This only dismisses the "Updating" display. Continue?'
      failed: 'Dismiss failed.'
</i18n>

<script>
import Notifications from '@/mixins/notifications';
import TrainingDataDownload from '@/mixins/trainingDataDownload';
import DateTimeFormatting from '@/mixins/dateTimeFormatting';

import ToolbarButton from '@/components/atoms/ToolbarButton';
import ConfirmDialog from '@/components/atoms/ConfirmDialog';
import GalleryViewHeader from '@/components/GalleryView/GalleryViewHeader';
import GalleryViewToolbar from '@/components/GalleryView/Toolbar';
import Layout from '@/components/GalleryView/Layout';
import InsectCountDifference from '@/components/GalleryView/InsectCountDifference';
import ReplacementNotification from '@/components/GalleryView/FlyCountView/ReplacementNotification';
import ThresholdAlertStatus from '@/components/GalleryView/FlyCountView/ThresholdAlertStatus';
import ContentHeader from '@/components/ContentHeader';
import InsectDetectionsEditor from '@/components/InsectDetectionsEditor';
import SensorIcon from '@/components/SensorIcon';
import PickedInsectsDraw from '@/components/PickedInsectsDraw';
import { ZoomProvider } from '@/components/Zoom';

import { isEditingTimedOut } from '@/libs/isEditingTimedOut';

const ORIGINAL_IMAGE_WIDTH = 2592;
const ORIGINAL_IMAGE_HEIGHT = 1944;

export default {
  name: 'FlyCountView',
  mixins: [
    Notifications,
    TrainingDataDownload,
    ZoomProvider,
    DateTimeFormatting,
  ],
  props: {
    editable: Boolean,
    event: Object,
    sensorType: String,
    hasPrevious: Boolean,
    hasNext: Boolean,
    title: String,
  },
  components: {
    ConfirmDialog,
    ContentHeader,
    GalleryViewHeader,
    GalleryViewToolbar,
    InsectCountDifference,
    InsectDetectionsEditor,
    Layout,
    PickedInsectsDraw,
    ReplacementNotification,
    SensorIcon,
    ThresholdAlertStatus,
    ToolbarButton,
  },
  computed: {
    timestamp() {
      return {
        date: this.formatDate(this.event.timestamp),
        time: this.formatTime(this.event.timestamp),
      };
    },
    detectionsUrl() {
      return `/api/v2/events/${this.event.id}/insect_detections/photo`;
    },
    difference() {
      return this.event.count_difference;
    },
    showTimeoutMenu() {
      return isEditingTimedOut(this.event, Date.now());
    },
  },
  methods: {
    download: async function (type) {
      switch (type) {
        case 'trainingData': {
          this.downloadAttachment(this.event.id, 'photo');
          break;
        }
        default: {
          this.save(this.$refs.drawer);
          break;
        }
      }
    },
    save: async function (canvas) {
      try {
        await canvas.export('download.png');
      } catch (error) {
        this.notifyError('msg.download.failed');
      }
    },
    async retry() {
      const originalStartedAt = this.event.updating_started_at;
      this.$emit('pollEvent', { updating_started_at: Math.floor(Date.now() / 1000) });
      try {
        const attachment = this.event.updating_attachment;
        const data = await this.api.fetchInsectDetectionResult(this.event.id, attachment);
        await this.api.updateInsectDetectionResult(this.event.id, attachment, data.positions);
        this.notifySuccess('msg.retry.success');
      } catch (error) {
        this.$emit('pollEvent', { updating_started_at: originalStartedAt });
        this.notifyError('msg.retry.failed');
      }
    },
    confirmDismiss() {
      this.dismissConfirmOpen = true;
    },
    async executeDismiss() {
      try {
        await this.api.dismissInsectDetectionUpdate(this.event.id);
        this.$emit('pollEvent', {
          updating: false,
          updating_started_at: null,
          updating_attachment: null,
        });
      } catch (error) {
        this.notifyError('msg.dismiss.failed');
      } finally {
        this.dismissConfirmOpen = false;
      }
    },
  },
  data() {
    return {
      editing: false,
      viewOriginal: false,
      dismissConfirmOpen: false,
      ORIGINAL_IMAGE_WIDTH,
      ORIGINAL_IMAGE_HEIGHT,
    };
  },
  inject: [
    'api',
    'downloadAttachment',
    'trap_effective_days',
    'trap_replaced_on',
  ],
  filters: {
    sign(value) {
      if (value > 0) {
        return `+ ${value}`;
      }
      if (value < 0) {
        return `- ${Math.abs(value)}`;
      }
      return '± 0';
    },
  },
  watch: {
    event(event, prev) {
      this.viewOriginal = false;
      if (event.id === prev.id) {
        this.$refs.drawer.refetch();
      }
    },
  },
};
</script>

<style scoped lang="sass">
@import 'vuetify/src/styles/styles.sass'

.result
  border: 2px solid #ccc

  &-item,
  ::v-deep .threshold-alert-status__field
    font-size: 15px
    font-weight: 500
    margin-bottom: 0.5rem
    display: flex
    align-items: center

.gallery-view__image
  display: inline-block
  height: 0
  width: 80%
  margin: 0 10%
  padding-bottom: calc(80% * (1944 / 2592))
  position: relative

  &__container
    position: absolute
    top: 0
    left: 0
    right: 0
    bottom: 0
</style>
